import { FieldDescriptionData } from '../../../shared/api/api';
import * as React from 'react';
import { Button, Loader } from 'semantic-ui-react';
import { ColumnSort, flexRender, HeaderGroup, Row, Table } from '@tanstack/react-table';
import { Expander } from './DatasetRegionsTable';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { localeData } from '../../../locales/data';
import { getFooterGroups, getHeaderGroups } from '../../../shared/table/table-helpers';

interface TableMarkupProps<DataType extends FieldDescriptionData> {
  data: DataType[];
  isFetching: boolean;
  headerGroups: HeaderGroup<DataType>[];
  sorting: ColumnSort[];
  table: Table<DataType>;
  getTrProps: (
    state: any,
    rowInfo?: Row<DataType>,
    column?: undefined
  ) => { onClick: (ev: React.MouseEvent<HTMLElement>) => void; className: string };
  isTableExpanded: boolean;
  getExpanderClickHandler: (rowId: string) => () => void;
  isExpandVisible: 0 | boolean | undefined;
  toggleTableExpanded: () => void;
  toggleButtonTextKey: string;
}

export function RegionTableMarkup<DataType extends FieldDescriptionData>({
  data,
  isFetching,
  headerGroups,
  sorting,
  table,
  getTrProps,
  isTableExpanded,
  getExpanderClickHandler,
  isExpandVisible,
  toggleTableExpanded,
  toggleButtonTextKey,
}: TableMarkupProps<DataType>) {
  return (
    <div>
      {data && isFetching && (
        <div className="data-table__stale-loader-container">
          <div className="data-table__stale-loader-background"></div>
          <Loader active={true} size="massive" className="data-table__stale-loader-spinner" />
        </div>
      )}
      <div className="block-table-container">
        <div className="ReactTable intro-step-alphas-3 -highlight">
          <div className="rt-table" role="grid">
            {getHeaderGroups(headerGroups, sorting)}

            <div
              {...{
                className: 'rt-tbody',
              }}
            >
              {table
                .getRowModel()
                .rows.filter(row => row.depth === 0)
                .map((row, i) => {
                  const trProps = getTrProps(undefined, row);
                  const isRowExpandable = isTableExpanded && row.getLeafRows().length > 1;
                  return (
                    <div className="rt-tr-group -showRows -hideAggregated" role="rowgroup" key={row.id}>
                      <div
                        {...{
                          key: row.id,
                          ...trProps,
                          className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'} ${trProps.className ?? ''}`,
                          role: 'row',
                        }}
                      >
                        {row.getVisibleCells().map((cell, i) => {
                          return (
                            // eslint-disable-next-line react/jsx-key
                            <div
                              {...{
                                key: cell.id,
                                // @ts-ignore
                                className: `rt-td ${cell.column.columnDef.meta?.className ?? ''} ${
                                  i === 0 && (isRowExpandable ? 'rt-expandable rt-pivot ' : 'rt-not-expandable rt-pivot')
                                }`,
                                role: 'gridcell',
                                style: {
                                  flex: `${cell.column.getSize()} 0 auto`,
                                  width: cell.column.getSize(),
                                },
                                ...(i === 0 && isRowExpandable && { onClick: getExpanderClickHandler(cell.row.id) }),
                              }}
                            >
                              <div>
                                {i === 0 &&
                                  (isRowExpandable ? (
                                    <Expander isOpen={row.getIsExpanded()} />
                                  ) : (
                                    <div className="rt-expander-placeholder"></div>
                                  ))}
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {row.getIsExpanded() && isRowExpandable && (
                        <div className="rt-tr-group -showRows -hideAggregated" role="rowgroup">
                          {row.getLeafRows().map(leafRow => {
                            const leafRowProps = getTrProps(undefined, leafRow);
                            return (
                              <div
                                key={leafRow.id}
                                {...{
                                  role: 'row',
                                  ...leafRowProps,
                                  className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'} ${leafRowProps.className ?? ''}`,
                                }}
                              >
                                {leafRow.getVisibleCells().map((cell, i) => (
                                  // eslint-disable-next-line react/jsx-key
                                  <div
                                    {...{
                                      key: cell.id,
                                      className: `rt-td`,
                                      role: 'gridcell',
                                      style: {
                                        flex: `${cell.column.getSize()} 0 auto`,
                                        width: cell.column.getSize(),
                                      },
                                    }}
                                  >
                                    {i !== 0 && flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            {getFooterGroups(table)}
          </div>
        </div>
      </div>
      {isExpandVisible && (
        <div className={'data-single-entity-table__footer'}>
          <Button
            size="small"
            onClick={toggleTableExpanded}
            className={[
              addModifiers('button', ['secondary']),
              addModifiers('data-single-entity-table__toggle-button', [isTableExpanded ? 'active' : '']),
            ].join(' ')}
          >
            <FormattedMessageHelper tagName="span" id={toggleButtonTextKey} defaultMessage={localeData.en[toggleButtonTextKey]} />
          </Button>
        </div>
      )}
    </div>
  );
}
